import React, { useState, useEffect, useRef } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Snackbar,
  SnackbarContent,
  Typography,
  Avatar,
  Box,
  Paper,
  CircularProgress,
  Portal,
} from "@mui/material";
import ContentPasteSearchIcon from "@mui/icons-material/ContentPasteSearch";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DownloadForOfflineIcon from "@mui/icons-material/DownloadForOffline";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import DescriptionIcon from "@mui/icons-material/Description";
import ImageIcon from "@mui/icons-material/Image";
import ArticleIcon from "@mui/icons-material/Article";
import { UrlApi } from "./apirest";
import { logEventos } from "./services";

/**
 * UploadFiles2 – Componente reutilizable de adjuntos de archivos.
 *
 * Props:
 * ─────────────────────────────────────────────────────────────────
 * openclose       {boolean}  Controla si el diálogo está abierto.
 * mostrar         {boolean}  Habilita agregar / eliminar archivos.
 *                            Si es false, el diálogo es solo-lectura.
 * onClose         {func}     Callback al cerrar el diálogo.
 * title           {string}   Título del diálogo. Default: "Archivos Adjuntos".
 *
 * ── Endpoints de carga (usar uno de los tres) ──────────────────────
 * fileId          {any}      /uploadfiles/todownload/:fileId
 * fileIdcompra + filesId     /uploadfiles/todownloadReq/:fileIdcompra/:filesId
 * fileIdcoso      {any}      /uploadfiles/toOrden/:fileIdcoso
 *
 * ── logEventos (opcional) ─────────────────────────────────────────
 * logConfig       {object}   Configuración para logEventos.
 *   logConfig.accionGuardar  {string}  Acción al guardar.   Default: "Guardar adjuntos"
 *   logConfig.accionBorrar   {string}  Acción al borrar.    Default: "Borrar adjunto"
 *   logConfig.modulo         {string}  Módulo del sistema.  Default: "Adjuntos"
 *   logConfig.descripcion    {string}  Descripción general. Default: "Gestión de archivos adjuntos"
 */
function UploadFiles2(props) {
  // ── Estado ──────────────────────────────────────────────────────
  const [openclose, setOpenClose] = useState(props.openclose);
  const [mostrar, setMostrar] = useState(props.mostrar);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [snack, setSnack] = useState(false);
  const [mensaje, setMensaje] = useState("");
  const [colortexto, setColorTexto] = useState("white");
  const [colorfondo, setColorFondo] = useState("black");
  const [archivos, setArchivos] = useState([]);
  const [loadingSizes, setLoadingSizes] = useState(true);
  const [imageUrl, setImageUrl] = useState("");
  const fileInputRef = useRef(null);
  const [previewUrls, setPreviewUrls] = useState({});

  // ── Configuración de logEventos ─────────────────────────────────
  const logConfig = {
    accionGuardar: "Guardar adjuntos",
    accionBorrar: "Borrar adjunto",
    modulo: "Adjuntos",
    descripcion: "Gestión de archivos adjuntos",
    ...props.logConfig,
  };

  // ── Sincronizar props externas ───────────────────────────────────
  useEffect(() => {
    setOpenClose(props.openclose);
    setMostrar(props.mostrar);
  }, [props.openclose, props.mostrar]);

  // ── Helpers: tamaño de archivos remotos ─────────────────────────
  const obtenerTamañoArchivoRemoto = async (id_archivo, nombre) => {
    try {
      const nombreCodificado = encodeURIComponent(nombre);
      const url = `${UrlApi}/uploadfiles/uploads/${id_archivo}-${nombreCodificado}`;

      const response = await fetch(url, { method: "HEAD", credentials: "include" });

      if (response.ok) {
        const contentLength = response.headers.get("Content-Length");
        if (contentLength) return parseInt(contentLength, 10);

        // Fallback: GET para obtener el blob y medir su tamaño
        try {
          const getResponse = await fetch(url, { method: "GET", credentials: "include" });
          if (getResponse.ok) {
            const blob = await getResponse.blob();
            return blob.size;
          }
        } catch (getError) {
          console.error(`Error en GET alternativo para ${nombre}:`, getError);
        }
      }
      return 0;
    } catch (error) {
      console.error(`Error obteniendo tamaño para ${nombre}:`, error);
      return 0;
    }
  };

  const obtenerTamañosEnSegundoPlano = async (data) => {
    try {
      const archivosConTamaños = await Promise.all(
        (data || []).map(async (archivo) => {
          try {
            const tamaño = await obtenerTamañoArchivoRemoto(
              archivo.id_archivo,
              archivo.nombre,
            );
            return {
              ...archivo,
              tamaño,
              nombre: archivo.nombre || "archivo_sin_nombre",
              extension: archivo.extension || "application/octet-stream",
              tamañoCargado: true,
            };
          } catch {
            return {
              ...archivo,
              tamaño: 0,
              nombre: archivo.nombre || "archivo_sin_nombre",
              extension: archivo.extension || "application/octet-stream",
              tamañoCargado: true,
            };
          }
        }),
      );
      setArchivos(archivosConTamaños);
      setLoadingSizes(false);
    } catch (error) {
      console.error("Error obteniendo tamaños en segundo plano:", error);
      setLoadingSizes(false);
    }
  };

  // ── Cargar archivos existentes (soporta los 3 endpoints) ────────
  useEffect(() => {
    if (!openclose) return;

    let url = null;

    if (props.fileId) {
      url = `${UrlApi}/uploadfiles/todownload/${props.fileId}`;
    } else if (props.fileIdcompra && props.filesId) {
      url = `${UrlApi}/uploadfiles/todownloadReq/${props.fileIdcompra}/${props.filesId}`;
    } else if (props.fileIdcoso) {
      url = `${UrlApi}/uploadfiles/toOrden/${props.fileIdcoso}`;
    }

    if (!url) return;

    setLoadingSizes(true);

    fetch(url)
      .then((res) => res.json())
      .then((data) => {
        const archivosIniciales = data || [];

        // Mostrar archivos sin tamaño de inmediato
        setArchivos(
          archivosIniciales.map((archivo) => ({
            ...archivo,
            tamaño: 0,
            nombre: archivo.nombre || "archivo_sin_nombre",
            extension: archivo.extension || "application/octet-stream",
            tamañoCargado: false,
          })),
        );

        // Cargar tamaños en segundo plano
        obtenerTamañosEnSegundoPlano(archivosIniciales);
      })
      .catch((error) => {
        console.error("Error cargando archivos:", error);
        setArchivos([]);
        setLoadingSizes(false);
      });
  }, [
    snack,
    openclose,
    props.fileId,
    props.fileIdcompra,
    props.filesId,
    props.fileIdcoso,
  ]);

  // ── Limpiar blob URLs al desmontar ───────────────────────────────
  useEffect(() => {
    return () => {
      Object.values(previewUrls).forEach((url) => {
        if (url?.startsWith("blob:")) URL.revokeObjectURL(url);
      });
    };
  }, [previewUrls]);

  // ── Helpers: formateo y extensiones ─────────────────────────────
  const formatFileSize = (bytes) => {
    const bytesNum = Number(bytes);
    if (isNaN(bytesNum) || bytesNum <= 0) return "Calculando...";
    const unidades = ["B", "KB", "MB", "GB"];
    let i = 0;
    let tamaño = bytesNum;
    while (tamaño >= 1024 && i < unidades.length - 1) {
      tamaño /= 1024;
      i++;
    }
    return `${tamaño.toFixed(i > 0 ? 1 : 0)} ${unidades[i]}`;
  };

  const getFileExtension = (fileType, fileName = "") => {
    if (!fileType && fileName) {
      const lastDot = fileName.lastIndexOf(".");
      if (lastDot !== -1) {
        const ext = fileName.substring(lastDot + 1).toUpperCase();
        return ext.length <= 5 ? ext : "Archivo";
      }
      return "Archivo";
    }

    const mimeTypeMap = {
      "application/pdf": "PDF",
      "image/jpeg": "JPEG",
      "image/jpg": "JPG",
      "image/png": "PNG",
      "image/gif": "GIF",
      "image/bmp": "BMP",
      "application/msword": "DOC",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "DOCX",
      "application/vnd.ms-excel": "XLS",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "XLSX",
      "application/vnd.ms-powerpoint": "PPT",
      "application/vnd.openxmlformats-officedocument.presentationml.presentation": "PPTX",
      "text/plain": "TXT",
      "text/csv": "CSV",
      "application/zip": "ZIP",
      "application/x-rar-compressed": "RAR",
      "application/octet-stream": "Archivo",
    };

    const lower = fileType?.toLowerCase();
    if (lower && mimeTypeMap[lower]) return mimeTypeMap[lower];

    // Tipos de Office con nombres largos
    if (lower?.includes("wordprocessingml.document")) return "DOCX";
    if (lower?.includes("spreadsheetml.sheet")) return "XLSX";
    if (lower?.includes("presentationml.presentation")) return "PPTX";

    // Extraer del tipo MIME genérico
    if (lower?.includes("/")) {
      const subtype = lower.split("/")[1];
      if (subtype.includes("vnd.")) {
        const parts = subtype.split(".");
        return parts[parts.length - 1].toUpperCase();
      }
      return subtype.toUpperCase().split(".").pop();
    }

    // Último recurso: extensión del nombre
    if (fileName) {
      const parts = fileName.split(".");
      if (parts.length > 1) {
        const ext = parts.pop().toUpperCase();
        return ext.length <= 5 ? ext : "Archivo";
      }
    }

    return "Archivo";
  };

  // ── Helpers: iconos ──────────────────────────────────────────────
  const buildIconPaper = (icon, color) => (
    <Paper
      elevation={0}
      sx={{
        width: 48,
        height: 48,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        mr: 2,
        border: "1px solid #e0e0e0",
        borderRadius: 1,
        bgcolor: `${color}15`,
      }}
    >
      <Box sx={{ color, fontSize: 28 }}>{icon}</Box>
    </Paper>
  );

  const getFileTypeIcon = (file) => {
    const isPDF = file.type === "application/pdf";
    const isImage = file.type?.startsWith("image/");
    const isDocument =
      file.type?.includes("word") ||
      file.type?.includes("document") ||
      file.name?.endsWith(".doc") ||
      file.name?.endsWith(".docx");

    if (isPDF) return buildIconPaper(<PictureAsPdfIcon />, "#f44336");
    if (isImage) return buildIconPaper(<ImageIcon />, "#2196f3");
    if (isDocument) return buildIconPaper(<ArticleIcon />, "#1976d2");
    return buildIconPaper(<DescriptionIcon />, "#757575");
  };

  const getServerFileIcon = (archivo) => {
    const ext = archivo.extension?.toLowerCase() || "";
    const nombre = archivo.nombre?.toLowerCase() || "";

    if (ext.includes("pdf") || nombre.endsWith(".pdf"))
      return buildIconPaper(<PictureAsPdfIcon />, "#f44336");
    if (
      ext.includes("image") ||
      ext.includes("jpeg") ||
      ext.includes("jpg") ||
      ext.includes("png") ||
      nombre.match(/\.(jpg|jpeg|png|gif|bmp)$/)
    )
      return buildIconPaper(<ImageIcon />, "#2196f3");
    if (
      ext.includes("word") ||
      ext.includes("document") ||
      nombre.endsWith(".doc") ||
      nombre.endsWith(".docx")
    )
      return buildIconPaper(<ArticleIcon />, "#1976d2");

    return buildIconPaper(<DescriptionIcon />, "#757575");
  };

  const getFileThumbnail = (file, index) => {
    if (file.type?.startsWith("image/") && previewUrls[index]) {
      return (
        <Avatar
          variant="square"
          src={previewUrls[index]}
          sx={{
            width: 48,
            height: 48,
            mr: 2,
            border: "1px solid #e0e0e0",
            borderRadius: 1,
            bgcolor: "background.paper",
          }}
          alt={file.name}
        />
      );
    }
    return getFileTypeIcon(file);
  };

  // ── Handlers ─────────────────────────────────────────────────────
  const borraAdjunto = (e, id, archive) => {
    e.stopPropagation();
    e.preventDefault();
    if (imageUrl) setImageUrl("");

    logEventos(
      logConfig.accionBorrar,
      logConfig.modulo,
      logConfig.descripcion,
      localStorage.getItem("user"),
    );

    if (window.confirm(`¿Seguro de borrar el archivo ${archive}?`)) {
      fetch(UrlApi + "/uploadfiles/todownload/" + id, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
      }).then((res) =>
        res.ok
          ? (setMensaje("Archivo borrado"),
            setColorTexto("white"),
            setColorFondo("green"),
            setSnack(true))
          : (setMensaje("No se borró el archivo"),
            setColorTexto("white"),
            setColorFondo("red"),
            setSnack(true)),
      );
    }
  };

  const handleCloseSnack = (event, reason) => {
    if (reason === "clickaway") return;
    setSnack(false);
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    const newFiles = [];
    let duplicates = 0;
    let alreadyUploaded = 0;
    const newPreviewUrls = { ...previewUrls };

    files.forEach((newFile) => {
      const isDuplicate = pendingFiles.some(
        (existing) =>
          existing.name === newFile.name && existing.size === newFile.size,
      );
      const isAlreadyUploaded = archivos.some(
        (archivo) => archivo.nombre === newFile.name,
      );

      if (isDuplicate) {
        duplicates++;
      } else if (isAlreadyUploaded) {
        alreadyUploaded++;
      } else {
        newFiles.push(newFile);
        if (newFile.type.startsWith("image/")) {
          const previewIndex = pendingFiles.length + newFiles.length - 1;
          newPreviewUrls[previewIndex] = URL.createObjectURL(newFile);
        }
      }
    });

    if (Object.keys(newPreviewUrls).length > 0) setPreviewUrls(newPreviewUrls);

    if (newFiles.length > 0) {
      setPendingFiles((prev) => [...prev, ...newFiles]);
      let message = `${newFiles.length} archivo(s) agregado(s)`;
      if (duplicates > 0 || alreadyUploaded > 0) {
        message += ` (${duplicates} duplicado(s) omitido(s), ${alreadyUploaded} ya subido(s))`;
      }
      setMensaje(message);
      setColorTexto("white");
      setColorFondo("green");
      setSnack(true);
    } else if (duplicates > 0 || alreadyUploaded > 0) {
      setMensaje(
        `Todos los archivos seleccionados ya están en la lista o han sido subidos (${duplicates} duplicado(s), ${alreadyUploaded} ya subido(s))`,
      );
      setColorTexto("white");
      setColorFondo("orange");
      setSnack(true);
    }

    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const removePendingFile = (index) => {
    if (previewUrls[index]) {
      URL.revokeObjectURL(previewUrls[index]);
      const newPreviewUrls = { ...previewUrls };
      delete newPreviewUrls[index];
      setPreviewUrls(newPreviewUrls);
    }

    const newFiles = [...pendingFiles];
    const removedFile = newFiles.splice(index, 1)[0];
    setPendingFiles(newFiles);

    setMensaje(`Archivo "${removedFile.name}" eliminado de la lista`);
    setColorTexto("white");
    setColorFondo("orange");
    setSnack(true);
  };

  const handleClose = () => {
    setOpenClose(false);
    Object.values(previewUrls).forEach((url) => {
      if (url?.startsWith("blob:")) URL.revokeObjectURL(url);
    });
    setPreviewUrls({});
    setPendingFiles([]);
    setMensaje("Guardar Archivos Cancelado");
    setColorTexto("white");
    setColorFondo("red");
    if (props.onClose) props.onClose();
  };

  const handleUploadClick = () => {
    logEventos(
      logConfig.accionGuardar,
      logConfig.modulo,
      logConfig.descripcion,
      localStorage.getItem("user"),
    );

    if (pendingFiles.length === 0) {
      setMensaje("No hay archivos para guardar");
      setColorTexto("white");
      setColorFondo("red");
      setSnack(true);
      return;
    }

    // Determinar el uid según el endpoint activo
    const uid = props.fileId ?? props.fileIdcompra ?? props.fileIdcoso;

    const data = new FormData();
    data.append("uid", uid);
    pendingFiles.forEach((file) => data.append("archivo", file));

    fetch(UrlApi + "/uploadfiles/uploadfiles", { method: "POST", body: data })
      .then((res) => {
        if (!res.ok) throw new Error(`Error en la respuesta: ${res.status}`);
        return res.json();
      })
      .then((data) => {
        Object.values(previewUrls).forEach((url) => {
          if (url?.startsWith("blob:")) URL.revokeObjectURL(url);
        });
        setPreviewUrls({});
        setMensaje(
          data.message ||
            `${pendingFiles.length} archivo(s) subido(s) exitosamente`,
        );
        setColorTexto("white");
        setColorFondo("green");
        setSnack(true);
        setPendingFiles([]);
      })
      .catch((err) => {
        console.error("Error al subir archivos:", err);
        setMensaje("Error al subir archivos. Por favor, intente nuevamente.");
        setColorTexto("white");
        setColorFondo("red");
        setSnack(true);
      });
  };

  // ── Ver / Descargar archivos ─────────────────────────────────────
  const fetchPdf = async (archivo) => {
    const response = await fetch(UrlApi + "/uploadfiles/uploadspdf/" + archivo);
    if (response.ok) {
      const url = URL.createObjectURL(await response.blob());
      window.open(url, "_blank", "noreferrer");
    }
  };

  const fetchImage = async (archivo) => {
    const response = await fetch(UrlApi + "/uploadfiles/uploads/" + archivo);
    if (response.ok) {
      const url = URL.createObjectURL(await response.blob());
      window.open(url, "_blank", "noreferrer");
    }
  };

  const viewFiles = async (archivo, extension) => {
    try {
      extension === "application/pdf"
        ? await fetchPdf(archivo)
        : await fetchImage(archivo);
    } catch (error) {
      console.error("Error al ver archivo:", error);
    }
  };

  const downloadFiles = async (archivo) => {
    try {
      const response = await fetch(UrlApi + "/uploadfiles/download/" + archivo);
      if (response.ok) {
        const url = window.URL.createObjectURL(await response.blob());
        const a = document.createElement("a");
        a.href = url;
        a.download = archivo.split("-").pop() || archivo;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error("Error al descargar archivo:", error);
    }
  };

  // ── Botón de agregar archivos ────────────────────────────────────
  const FileInputButton = () => (
    <Button
      component="label"
      size="small"
      variant="outlined"
      sx={{
        border: "1px solid white",
        borderRadius: 1,
        color: "white",
        fontStyle: "italic",
        "&:hover": {
          border: "1px solid white",
          backgroundColor: "rgba(255,255,255,0.1)",
        },
      }}
    >
      <input
        hidden
        type="file"
        multiple
        onChange={handleFileChange}
        ref={fileInputRef}
        accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
      />
      Agregar archivos
    </Button>
  );

  // ── Render ───────────────────────────────────────────────────────
  return (
    <>
      {openclose && (
        <div>
          <Dialog
            open={openclose}
            onClose={handleClose}
            fullWidth
            maxWidth="md"
            PaperProps={{
              sx: { height: 600, maxHeight: "80vh" },
            }}
          >
            <DialogTitle
              sx={{
                backgroundColor: "#0979B0",
                border: "1px solid white",
                color: "white",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <div style={{ display: "flex", alignItems: "center" }}>
                <ContentPasteSearchIcon sx={{ mr: 1 }} />
                <b>{props.title ?? "Archivos Adjuntos"}</b>
              </div>
              {mostrar && <FileInputButton />}
            </DialogTitle>

            <DialogContent dividers sx={{ p: 2 }}>
              {/* Archivos pendientes de subir */}
              {pendingFiles.length > 0 && (
                <>
                  <Typography
                    variant="subtitle1"
                    sx={{ mb: 2, color: "primary.main", fontWeight: "bold" }}
                  >
                    Archivos pendientes de subir ({pendingFiles.length})
                  </Typography>
                  <Box sx={{ mb: 3 }}>
                    {pendingFiles.map((file, i) => (
                      <Paper
                        key={i}
                        elevation={0}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          p: 1.5,
                          mb: 1,
                          borderRadius: 1,
                          border: "1px solid #e0e0e0",
                          backgroundColor: i % 2 === 0 ? "white" : "#f8f9fa",
                          "&:hover": { backgroundColor: "#f0f0f0" },
                        }}
                      >
                        {getFileThumbnail(file, i)}

                        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                          <Typography
                            variant="body1"
                            sx={{
                              fontWeight: 500,
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {file.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formatFileSize(file.size)} •{" "}
                            {getFileExtension(file.type, file.name)}
                          </Typography>
                        </Box>

                        {mostrar && (
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              e.preventDefault();
                              removePendingFile(i);
                            }}
                            sx={{ ml: 1 }}
                            title="Eliminar de la lista"
                          >
                            <DeleteIcon color="error" fontSize="small" />
                          </IconButton>
                        )}
                      </Paper>
                    ))}
                  </Box>
                  <Divider sx={{ mb: 3 }} />
                </>
              )}

              {/* Archivos ya subidos */}
              <Typography
                variant="subtitle1"
                sx={{ mb: 2, fontWeight: "bold" }}
              >
                Archivos subidos ({archivos.length})
              </Typography>

              {archivos.length > 0 ? (
                <Box>
                  {archivos.map((archivo, i) => (
                    <Paper
                      key={archivo.id}
                      elevation={0}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        p: 1.5,
                        mb: 1,
                        borderRadius: 1,
                        border: "1px solid #e0e0e0",
                        backgroundColor: i % 2 === 0 ? "white" : "#f8f9fa",
                        "&:hover": { backgroundColor: "#f0f0f0" },
                      }}
                    >
                      {getServerFileIcon(archivo)}

                      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                        <Typography
                          variant="body1"
                          sx={{
                            fontWeight: 500,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {archivo.nombre}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {loadingSizes || !archivo.tamañoCargado ? (
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                              }}
                            >
                              <CircularProgress size={10} />
                              Calculando tamaño...
                            </Box>
                          ) : archivo.tamaño > 0 ? (
                            `${formatFileSize(archivo.tamaño)} • ${getFileExtension(archivo.extension, archivo.nombre)}`
                          ) : (
                            getFileExtension(archivo.extension, archivo.nombre)
                          )}
                        </Typography>
                      </Box>

                      <Box
                        sx={{ display: "flex", gap: 0.5 }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <IconButton
                          size="small"
                          color="primary"
                          disabled={
                            !["application/pdf", "image/jpeg", "image/png"].includes(
                              archivo.extension,
                            )
                          }
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            viewFiles(
                              `${archivo.id_archivo}-${encodeURIComponent(archivo.nombre)}`,
                              archivo.extension,
                            );
                          }}
                          title="Ver archivo"
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>

                        <IconButton
                          size="small"
                          color="success"
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            downloadFiles(
                              `${archivo.id_archivo}-${encodeURIComponent(archivo.nombre)}`,
                            );
                          }}
                          title="Descargar archivo"
                        >
                          <DownloadForOfflineIcon fontSize="small" />
                        </IconButton>

                        {mostrar && (
                          <IconButton
                            size="small"
                            color="error"
                            onClick={(e) => borraAdjunto(e, archivo.id, archivo.nombre)}
                            title="Eliminar archivo"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        )}
                      </Box>
                    </Paper>
                  ))}
                </Box>
              ) : (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  align="center"
                  sx={{ py: 4, fontStyle: "italic" }}
                >
                  No hay archivos adjuntos
                </Typography>
              )}
            </DialogContent>

            <DialogActions sx={{ p: 2, borderTop: "1px solid #e0e0e0" }}>
              <Button
                onClick={handleClose}
                variant="outlined"
                color="error"
                sx={{ minWidth: 100 }}
              >
                Volver
              </Button>
              {mostrar && (
                <Button
                  onClick={handleUploadClick}
                  variant="contained"
                  color="success"
                  disabled={pendingFiles.length === 0}
                  sx={{ minWidth: 120 }}
                >
                  Guardar
                  {pendingFiles.length > 0 ? ` (${pendingFiles.length})` : ""}
                </Button>
              )}
            </DialogActions>
          </Dialog>
        </div>
      )}

      <Portal>
        <Snackbar
          open={snack}
          autoHideDuration={3000}
          onClose={handleCloseSnack}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
          sx={{ zIndex: 9999 }}
        >
          <SnackbarContent
            style={{
              backgroundColor: colorfondo,
              color: colortexto,
              fontWeight: "bold",
            }}
            message={mensaje}
          />
        </Snackbar>
      </Portal>
    </>
  );
}

export default UploadFiles2;